/**
 * API - S3에서 캐시된 데이터를 가져오는 로직
 *
 * 이 파일은 Next.js 빌드/런타임에서 사용됩니다.
 * WordPress에서 데이터를 가져와 캐시를 갱신하려면 wordpress-api.ts를 사용하세요.
 */

import { fetchWordpressCacheData } from './wordpress-cache';

export type FetchAPIOptions<T extends boolean = false> = T extends false
  ? {
      refresh?: boolean;
      label?: string;
      prodFilter?: boolean;
      strict?: boolean;
    }
  : {
      refresh?: boolean;
      label: string;
      prodFilter?: boolean;
      strict?: boolean;
    };

/**
 * S3에서 캐시된 데이터를 가져옵니다.
 * Next.js 빌드/런타임에서 사용됩니다.
 */
export async function fetchFromCache(label: string): Promise<any | null> {
  const cachedData = await fetchWordpressCacheData(label);
  return cachedData;
}

// ============================================
// Pagination Helper for Cache Fetch
// ============================================

interface PaginatedCacheConfig<T> {
  getPageInfo: (data: any) => {
    hasNextPage: boolean;
  };
  getItems: (data: any) => T[];
  postCount: number;
}

/**
 * 페이지네이션된 캐시 데이터를 모두 가져옵니다.
 * Next.js 빌드/런타임에서 사용됩니다.
 *
 * ⚠️ 기본 동작은 **없는 페이지에서 조용히 멈추고 그때까지 받은 것만 반환**한다. 일시적 캐시
 * 미스(CloudFront 가 없는 객체에 주는 403)로 중간 페이지가 빠지면 "그럴듯하지만 부족한" 목록이
 * 나온다 — 화면에 글이 몇 개 덜 보이는 정도라 목록 렌더에는 감수할 수 있다.
 * 그 결과로 **다른 캐시를 만들어 영속화하는 호출자**는 감수할 수 없다(틀린 집계가 박제된다).
 * 그런 호출자는 `strict: true` 로 불완전 읽기를 에러로 받는다.
 */
export const fetchAllFromCache = async <T>(
  config: PaginatedCacheConfig<T>,
  options: FetchAPIOptions<true>,
): Promise<T[]> => {
  let allItems: T[] = [];
  let hasNextPage = true;
  let fromIndex = 1;

  while (hasNextPage) {
    const label = `${options.label}-${fromIndex}`;

    const data = await fetchFromCache(label);

    if (!data) {
      // 여기 도달했다는 건 hasNextPage:false 를 보기 전에 페이지가 사라졌다는 뜻 = 불완전
      if (options.strict) {
        throw new Error(
          `캐시 페이지를 찾지 못했습니다: ${label} — 불완전한 목록을 반환하지 않고 중단합니다`,
        );
      }
      break;
    }

    const pageInfo = config.getPageInfo(data);
    const newItems = config.getItems(data);

    allItems = [...allItems, ...newItems];
    hasNextPage = pageInfo.hasNextPage;
    fromIndex += config.postCount;
  }

  return allItems;
};
